import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Clock, ArrowUpRight, MessageCircle, Linkedin, Facebook, Instagram, Copy } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { useEmailCompose } from "@/context/EmailComposeContext";

const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=Via+Nino+Bixio+182+Monopoli+BA";

const INFO = [
  { icon: MapPin, k: "Sede", v: "Via Nino Bixio, 182 — Monopoli (BA)", href: MAPS_URL, external: true, testid: "contatti-sede" },
  { icon: Phone, k: "Cellulare", v: "+39 351 926 1622", href: "tel:+393519261622", testid: "contatti-cellulare" },
  { icon: Mail, k: "Commerciale", v: "commerciale@greenhousesolutionsspa.com", email: "commerciale@greenhousesolutionsspa.com", testid: "contatti-email-commerciale" },
  { icon: Mail, k: "Amministrazione", v: "amministrazione@greenhousesolutionsspa.com", email: "amministrazione@greenhousesolutionsspa.com", testid: "contatti-email-amministrazione" },
  { icon: Mail, k: "Segreteria", v: "segreteria@greenhousesolutionsspa.com", email: "segreteria@greenhousesolutionsspa.com", testid: "contatti-email-segreteria" },
  { icon: Clock, k: "Orari ufficio", v: "Lun–Ven · 09:00–17:00", extra: { label: "Telefono ufficio", value: "080 521 1927", href: "tel:+390805211927", testid: "contatti-telefono-ufficio" } },
];

const PEC = { label: "PEC", value: "greenhousesolutionsspa@pec.it", testid: "contatti-email-pec" };

const copyPecToClipboard = async () => {
  const value = PEC.value;
  const fallbackCopy = () => {
    try {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, value.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };
  let ok = false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      ok = true;
    }
  } catch { /* fall through to legacy copy */ }
  if (!ok) ok = fallbackCopy();
  if (ok) {
    toast.success("PEC copiata negli appunti", {
      description: "Incollala nel tuo client di posta certificata per inviare il messaggio.",
    });
  } else {
    toast.error("Impossibile copiare la PEC", { description: value });
  }
};

const WHATSAPP = [
  { label: "Commerciale", n: "+39 351 926 1622" },
  { label: "Amministrazione", n: "+39 338 468 8841" },
  { label: "Segreteria", n: "+39 327 672 0303" },
];

const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/greenhouse-solutions-spa/", testid: "social-linkedin" },
  { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/share/18xSY7wsha/", testid: "social-facebook" },
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/ghs.spa?utm_source=qr&igsh=em92MnJudXR3dzdz", testid: "social-instagram" },
];

function InfoCard({ c }) {
  const { open: openEmail } = useEmailCompose();
  const inner = (
    <>
      <c.icon className="w-8 h-8 text-primary shrink-0 mt-1" />
      <div className="min-w-0">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
          {c.k}{(c.href || c.email) && <ArrowUpRight className="w-3.5 h-3.5" />}
        </p>
        <p className="font-head font-bold text-lg md:text-xl tracking-tight break-words">{c.v}</p>
        {c.extra && (
          <a
            href={c.extra.href}
            data-testid={c.extra.testid}
            className="mt-3 inline-flex items-center gap-2 group/extra"
            onClick={(e) => e.stopPropagation()}
          >
            <Phone className="w-5 h-5 text-primary shrink-0" />
            <span className="min-w-0">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{c.extra.label}</span>
              <span className="block font-head font-bold text-lg md:text-xl tracking-tight group-hover/extra:text-primary transition-colors">{c.extra.value}</span>
            </span>
          </a>
        )}
      </div>
    </>
  );
  const cls = "bg-card p-10 h-full flex items-start gap-6 transition-colors";
  if (c.email) {
    return (
      <button
        type="button"
        onClick={() => openEmail(c.email)}
        data-testid={c.testid}
        aria-label={`Scrivi email a ${c.email}`}
        className={`${cls} hover:bg-secondary/60 group text-left w-full cursor-pointer bg-card border-0`}
      >
        {inner}
      </button>
    );
  }
  if (c.href) {
    return (
      <a
        href={c.href}
        data-testid={c.testid}
        {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className={`${cls} hover:bg-secondary/60 group`}
      >
        {inner}
      </a>
    );
  }
  return <div className={cls}>{inner}</div>;
}

export default function Contatti() {
  return (
    <div data-testid="contatti-page">
      <PageHeader
        index="/ 06"
        title="Contatti"
        lines={["Parliamo", "del tuo", "progetto."]}
        subtitle="Operiamo da Monopoli in tutta la Puglia e sull'intero territorio nazionale. Contattaci per un sopralluogo o un preventivo gratuito."
        logo
      />

      <section className="pb-24 md:pb-32 bg-background">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-4">
          {INFO.map((c, i) => (
            <Reveal key={c.k} delay={(i % 2) * 0.08}>
              <InfoCard c={c} />
            </Reveal>
          ))}
        </div>

        {/* WHATSAPP */}
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 mt-6">
          <Reveal>
            <div className="bg-card p-10">
              <div className="flex items-center gap-3 mb-6">
                <MessageCircle className="w-7 h-7 text-primary" />
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">WhatsApp</p>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {WHATSAPP.map((item) => (
                  <a
                    key={item.n}
                    href={`https://wa.me/${item.n.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`whatsapp-${item.n.replace(/[^0-9]/g, "")}`}
                    className="group flex items-center justify-between gap-2 bg-background/60 px-5 py-4 hover:bg-primary/10 hover:text-primary transition-colors"
                  >
                    <span className="min-w-0">
                      <span className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{item.label}</span>
                      <span className="block font-head font-bold text-lg tracking-tight truncate">{item.n}</span>
                    </span>
                    <ArrowUpRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        {/* PEC — voce singola (copia negli appunti, i PEC non aprono con mailto:) */}
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 mt-6">
          <Reveal>
            <button
              type="button"
              onClick={copyPecToClipboard}
              data-testid={PEC.testid}
              aria-label="Copia indirizzo PEC negli appunti"
              className="group block w-full text-left bg-card p-8 md:p-10 hover:bg-primary/5 transition-colors cursor-pointer"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <Mail className="w-7 h-7 text-primary shrink-0" />
                  <div className="min-w-0">
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Posta certificata (PEC) — click per copiare</p>
                    <p className="font-head font-bold text-lg md:text-xl tracking-tight mt-1 break-all">{PEC.value}</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors shrink-0">
                  <Copy className="w-4 h-4" /> Copia
                </span>
              </div>
            </button>
          </Reveal>
        </div>

        {/* SOCIAL */}
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 mt-6">
          <Reveal>
            <div className="bg-card p-10">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">Seguici sui social</p>
              <div className="grid sm:grid-cols-3 gap-4">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={s.testid}
                    className="group flex items-center justify-between gap-2 bg-background/60 px-5 py-4 hover:bg-primary/10 hover:text-primary transition-colors"
                  >
                    <span className="flex items-center gap-3">
                      <s.icon className="w-6 h-6 text-primary" />
                      <span className="font-head font-bold text-lg tracking-tight">{s.label}</span>
                    </span>
                    <ArrowUpRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        {/* PARTNERSHIP PA CARD */}
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 mt-6">
          <Reveal>
            <a
              href="https://pacard.it/vetrina/16026-greenhouse-solutions-spa-imprese-edili-monopoli-ba"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contatti-pacard"
              aria-label="Vetrina PA card: convenzioni e sconti per dipendenti Pubblica Amministrazione"
              className="group block bg-primary text-primary-foreground p-10 md:p-12 hover:brightness-95 transition-all"
            >
              <div className="flex flex-col items-start md:flex-row md:items-center gap-8 md:gap-12">
                <img
                  src="/images/pacard-logo.webp"
                  alt="PA card by Convenzioni Dipendenti PA"
                  width="640"
                  height="176"
                  className="w-auto h-auto max-h-16 md:max-h-20 max-w-[220px] md:max-w-none shrink-0 object-contain"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-primary-foreground/80 mb-2">Partnership · Convenzione ufficiale</p>
                  <h3 className="font-head font-black text-2xl md:text-3xl tracking-tightest leading-tight">
                    Vantaggi e sconti per i dipendenti della Pubblica Amministrazione
                  </h3>
                  <p className="text-primary-foreground/85 mt-3 text-sm md:text-base max-w-2xl">
                    Sei un dipendente pubblico? Visita la nostra vetrina su PA card e scopri le condizioni a te riservate su ristrutturazioni e interventi tecnici.
                  </p>
                </div>
                <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary-foreground group-hover:translate-x-1 transition-transform shrink-0">
                  Vai alla vetrina <ArrowUpRight className="w-5 h-5" />
                </span>
              </div>
            </a>
          </Reveal>
        </div>

        {/* PARTNERSHIP INPRIMEPAY */}
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 mt-6">
          <Reveal>
            <div
              data-testid="contatti-inprimepay"
              className="inprime-card bg-[#0d3d3f] text-background p-8 md:p-12 overflow-hidden"
            >
              <div className="flex flex-col items-start md:flex-row md:items-center gap-8 md:gap-12">
                <div className="flex-1 min-w-0 order-2 md:order-1">
                  <p className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-[#3fe0d0] mb-3">Partnership · Pagamento a rate</p>
                  <h3 className="font-head font-black text-2xl md:text-4xl tracking-tightest leading-[1.05]">
                    Realizza il tuo progetto oggi.<br />
                    <span className="text-[#3fe0d0]">Rateizza l'investimento nel tempo.</span>
                  </h3>
                  <p className="text-background/85 mt-4 text-sm md:text-base max-w-xl leading-relaxed">
                    Grazie alla partnership con <strong className="text-background">inprimePay</strong> puoi finanziare i lavori a rate con <strong className="text-background">Klarna</strong> e <strong className="text-background">Alma</strong> — transazioni verificate, sicure e trasparenti.
                  </p>
                </div>
                <img
                  src="/images/inprimepay-partnership.png"
                  alt="inprimePay — Paga a rate con Klarna e Alma. Transazioni veloci e sicure."
                  width="1200"
                  height="896"
                  className="w-full md:w-auto md:h-64 lg:h-72 max-w-full md:max-w-[520px] shrink-0 order-1 md:order-2 inprime-3d"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>
        </div>

        <div className="max-w-[1500px] mx-auto px-5 md:px-10 mt-16">
          <Reveal>
            <div className="bg-foreground text-background p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
              <h2 className="font-head font-black text-3xl md:text-5xl tracking-tightest max-w-xl leading-[0.95]">
                Ricevi un preventivo su misura, gratuito e senza impegno.
              </h2>
              <Link to="/preventivo" data-testid="contatti-cta" className="shrink-0 inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-widest px-8 py-5 hover:bg-background hover:text-foreground transition-colors">
                Richiedi Preventivo <ArrowUpRight className="w-5 h-5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
